import type { CookieState } from '@app/services/CookieManager'

import { createCookieName } from '@app/utils/cookieNames'
import { createCookie } from '@app/utils/cookies'
import { createSelector } from '@app/utils/createSelector'
import moment from '@app/utils/moment'

import type { ApiConsentTypeKey } from '@app/packages/api/v2/types/entities'
import type { UserConsentRequestItem } from '@app/packages/api/v2/types/params'
import { createPromise } from '@app/packages/thunkSelector/promiseState'
import { createLoadable, resolveThunkSelector } from '@app/packages/thunkSelector/thunkSelector'

import { callV2Api } from '@app/store/actions/api'
import { getCookies } from '@app/store/actions/contexts/cookies'
import { createReduxSlice } from '@app/store/redux_slice'
import { configSelector, isAppSelector } from '@app/store/selectors/misc'
import { profileUserIdSelector } from '@app/store/selectors/profile'
import { createThunk } from '@app/store/thunk'

import { structuredPoliciesSelector } from './policies'
import { isSupervisedSelector } from './session'
import { consentsSelector } from './user_consents'

const slice = createReduxSlice<{ value: boolean; hash: string }>('cookie_consent')

const cookieStateSlice = createReduxSlice<CookieState>('cookie_state')

export const setCookieState = (state: CookieState) => cookieStateSlice.set(state)
export const getCookieState = () => cookieStateSlice.get()

export const hasCookieConsentSelector = createSelector(
  [slice.selector, consentsSelector, profileUserIdSelector, isAppSelector, isSupervisedSelector, configSelector],
  (state, consentsState, userId, isApp, isSupervised, cfg) =>
    createLoadable(c => {
      if (isApp) return true
      if (isSupervised) return true
      if (cfg.isPolygon) return true
      c.unwrap(consentsState)
      const hash = [userId || ''].join(':')
      if (!state || state.hash !== hash) {
        return c.load(
          createPromise('5e2df04f-f93b-4262-8e63-76feffbe738d Cookie consent state restore', hash, async dispatch => {
            await dispatch(restoreConsent())
          })
        )
      }
      return state.value
    })
)

export const setConsent = (hasConsent: boolean, hash: string) =>
  createThunk(dispatch => {
    const cookies = dispatch(getCookies())
    if (hasConsent) cookies.enableConsent()
    dispatch(slice.set({ value: hasConsent, hash }))
  })

export const restoreConsent = () =>
  createThunk(async (dispatch, getState) => {
    const userId = profileUserIdSelector(getState())
    const hash = [userId || ''].join(':')

    const hasConsent = await (async () => {
      const { consents, enabled } = await dispatch(resolveThunkSelector(consentsSelector))
      const keys = consents.map(c => c.attributes.consent_type_key)
      const requiredKeys: ApiConsentTypeKey[] = ['cookies_essential', 'cookies_analytics', 'cookies_functional', 'cookies_marketing']
      const hasAllConsents = requiredKeys.every(key => keys.includes(key))
      if (hasAllConsents) return true
      const cookies = dispatch(getCookies())
      return !enabled || (await consentCookie.get(cookies)) || configSelector(getState()).isApp || isSupervisedSelector(getState())
    })()

    dispatch(setConsent(hasConsent, hash))
  })

export const getConsent = () =>
  createThunk(async dispatch => {
    const val = dispatch(slice.get())
    if (!val) await dispatch(restoreConsent())
    return dispatch(slice.get())!.value
  })

export const provideConsent = (text: string, source: string) =>
  createThunk(async (dispatch, getState) => {
    const { postUserConsentsBulk } = await import('@app/packages/api/v2/actions/user_consents')
    const userId = profileUserIdSelector(getState())
    const hash = [userId || ''].join(':')
    const types: ApiConsentTypeKey[] = ['cookies_analytics', 'cookies_essential', 'cookies_functional', 'cookies_marketing']
    const policies = (await dispatch(resolveThunkSelector(structuredPoliciesSelector))).filter(p => p.policy.attributes.key === 'cookies')
    const policiesIds = policies.map(p => p.policy.id)

    if (!policiesIds.length) throw new Error('No policies found for the given consent types')

    await dispatch(
      callV2Api(
        postUserConsentsBulk({
          user_consents: policiesIds.flatMap(id =>
            types.map((type): UserConsentRequestItem => ({
              consent_method: 'button',
              consent_type_key: type,
              policy_id: id,
              status: 'granted',
              consent_text: text,
              source,
            }))
          ),
        })
      )
    )

    dispatch(setConsent(true, hash))
    const cookies = dispatch(getCookies())
    await consentCookie.set(cookies, true)
  })

const consentCookie = createCookie<boolean>(
  createCookieName('kidsout__cookie_consent'),
  v => v === '1',
  v => (v ? '1' : '0'),
  () => ({ expires: moment().add(10, 'years'), ignoreConsent: true })
)
